import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Paper, Grid, TextField, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import useSnackbar from '../../Hooks/useSnackbar';
import { resetCharactersData, setActive, setCharacters, STORED_CHARACTERS } from '../../Slices/uniqueKillsDataSlice';

import Page from '../../Components/Page';
import SEO from '../../Components/SEO';
import CharacterItems from './CharacterItems';
import CharacterStats from './CharacterStats';
import useUniqueKillQueue from './useUniqueKillQueue';

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    button: {
        width: '100%',
        marginBottom: theme.spacing(2)
    },
    input: {
        width: '100%'
    },
    addButton: {
        width: '100%',
        marginRight: theme.spacing(2)
    }
}));

const getIdsForNames = async names => {
    const esiData = await axios.post('https://esi.evetech.net/latest/universe/ids/', names).then(r => r.data);

    let nameMap = {};
    if (esiData) {
        esiData.characters.forEach(esiResult => {
            nameMap[esiResult.name] = esiResult.id;
        });
    }

    return nameMap;
};

const UniqueKills = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [newCharacter, setNewCharacter] = useState('');
    const { characters, active: queueActive } = useSelector(state => state.uniqueKillsData);
    const characterCount = Object.keys(characters).length;
    const { openSnackbar } = useSnackbar();

    useEffect(() => {
        const storedInfo = localStorage.getItem(STORED_CHARACTERS);
        if (storedInfo) {
            dispatch(setCharacters(JSON.parse(storedInfo)));
        } else {
            localStorage.setItem(STORED_CHARACTERS, JSON.stringify(characters));
        }
    }, []);

    useUniqueKillQueue();

    const onCharChange = e => setNewCharacter(e.target.value);
    const addCharacter = () => {
        if (newCharacter) {
            // get all current character names
            const newCharacters = Object.keys(characters);

            newCharacters.push(newCharacter);

            getIdsForNames(newCharacters)
                .then(nameMap => {
                    openSnackbar(`Added "${newCharacter}" to the list`);

                    setNewCharacter('');
                    dispatch(setCharacters(nameMap));
                    storeCharacters(nameMap);
                })
                .catch(console.error);
        }
    };
    const storeCharacters = data => {
        localStorage.setItem(STORED_CHARACTERS, JSON.stringify(data));
    };

    const startChecking = () => {
        dispatch(resetCharactersData());
        dispatch(setActive(true));
    };

    const handleKeyPress = event => {
        if (event.key === 'Enter') addCharacter();
    };

    const NewCharacterSection = (
        <Grid item xs={12} md={8} lg={7} xl={6}>
            <Paper className={classes.paper}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={9} lg={10}>
                        <TextField
                            className={classes.input}
                            value={newCharacter}
                            onChange={onCharChange}
                            placeholder="Add another character to the list"
                            size="small"
                            onKeyPress={handleKeyPress}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} lg={2}>
                        <Button
                            className={classes.addButton}
                            variant="contained"
                            size="medium"
                            disabled={!newCharacter}
                            onClick={addCharacter}
                        >
                            Add
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );

    return (
        <Page title="Unique kills">
            <SEO
                title="Unique kills"
                path="/unique-kills"
                description="Calculate the total amount of unique kills across all your alts"
            />

            <Grid container justifyContent="center">
                {NewCharacterSection}
                <Grid item xs={12} />
                <Grid item xs={12} md={8} lg={7} xl={6}>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        disabled={characterCount < 1 || queueActive}
                        onClick={startChecking}
                    >
                        Start
                    </Button>
                </Grid>
                <Grid item xs={12} />
                <Grid item xs={12} md={8} lg={7} xl={6}>
                    <CharacterStats />
                </Grid>
                <Grid item xs={12} />
                <Grid item xs={12} md={8} lg={7} xl={6}>
                    <CharacterItems characters={characters} />
                </Grid>
            </Grid>
        </Page>
    );
};

export default UniqueKills;
